import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { VenueTag } from '../models/VenueTag'
import { venueTagSchema } from '../models/schema'
import {
  createVenueTag as createVenueTagAPI,
  deleteVenueTag as deleteVenueTagAPI,
  fetchVenueTagById,
  fetchVenueTags,
  updateVenueTag as updateVenueTagAPI,
} from '../services/VenueTagService'
import { AppDispatch, RootState } from './store'

const initialState: SBState<VenueTag> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'venueTag',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getVenueTagsSuccess,
  setQuery: setVenueTagQuery,
  reset: resetVenueTagState,
  resetQueryAndIds: resetVenueTagQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<RootState, { [key: number]: VenueTag }> = (
  state: RootState
) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectVenueTags = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedVenueTag = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectVenueTagById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectVenueTagsByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedVenueTag =
  (VenueTag: VenueTag | number) => async (dispatch: AppDispatch) =>
    dispatch(slice.actions.setSelectedId(getIdOrModelId<VenueTag>(VenueTag)))

export const getVenueTags = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchVenueTags(params),
    [venueTagSchema],
    slice.actions
  )

export const createVenueTag = (VenueTag: FormData) =>
  SBAPI.CreateDispatched(
    createVenueTagAPI(VenueTag),
    venueTagSchema,
    slice.actions
  )

export const getVenueTagWithId = (id: number) =>
  SBAPI.FetchDispatched<VenueTag>(
    fetchVenueTagById(id),
    venueTagSchema,
    slice.actions
  )

export const updateVenueTag = (VenueTag: FormData) =>
  SBAPI.UpdateDispatched<VenueTag>(
    updateVenueTagAPI(VenueTag),
    VenueTag,
    slice.actions
  )

export const deleteVenueTag = (VenueTag: VenueTag) =>
  SBAPI.DestroyDispatched<VenueTag>(
    deleteVenueTagAPI(VenueTag),
    VenueTag,
    slice.actions
  )
